import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Typography,
  Tag,
  Alert,
  Divider,
  message,
  Spin
} from 'antd';
import {
  GlobalOutlined,
  EuroCircleOutlined,
  HomeOutlined,
  TruckOutlined,
  ContainerOutlined
} from '@ant-design/icons';

import { unifiedShipmentsAPI, importerAPI, containerTypeAPI } from '../services/api.ts';
import { UnifiedShipmentType } from '../types.ts';

const { Option } = Select;
const { TextArea } = Input;

interface Supplier {
  id: number;
  name: string;
  origin_type_id?: number;
  origin_type?: {
    id: number;
    code: string;
    description: string;
  };
}

interface Importer {
  id: number;
  ragione_sociale: string;
}

interface Warehouse {
  id: number;
  description: string;
  cod_mag?: string;
  is_nationalized: boolean;
}

interface TransportType {
  id: number;
  code: string;
  description: string;
}

interface ShipmentStatus {
  id: number;
  code: string;
  description: string;
}

interface ContainerType {
  id: number;
  code: string;
  description: string;
}

interface ShipmentCreateModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ShipmentCreateModal: React.FC<ShipmentCreateModalProps> = ({
  visible,
  onClose,
  onSuccess
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Dati di lookup
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [importers, setImporters] = useState<Importer[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [transportTypes, setTransportTypes] = useState<TransportType[]>([]);
  const [containerTypes, setContainerTypes] = useState<ContainerType[]>([]);
  const [statuses, setStatuses] = useState<ShipmentStatus[]>([]);

  // Stato derivato
  const [shipmentType, setShipmentType] = useState<UnifiedShipmentType | null>(null);
  const [selectedTransportTypeCode, setSelectedTransportTypeCode] = useState<string | null>(null);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);

  // Carica dati di lookup
  useEffect(() => {
    if (visible) {
      loadLookupData();
    }
  }, [visible]);

  const loadLookupData = async () => {
    setLoading(true);
    try {
      const [suppliersRes, importersRes, transportTypesRes, containerTypesRes, statusesRes] = await Promise.all([
        unifiedShipmentsAPI.getSuppliersWithReadyOrders(),
        importerAPI.getImporters(),
        unifiedShipmentsAPI.getTransportTypes(),
        containerTypeAPI.getContainerTypes({ is_active: true }),
        unifiedShipmentsAPI.getStatuses()
      ]);

      setSuppliers(suppliersRes || []);
      setImporters(importersRes || []);
      setTransportTypes(transportTypesRes || []);
      setContainerTypes(containerTypesRes || []);
      setStatuses(statusesRes || []);
    } catch (error: any) {
      console.error('Error loading lookup data:', error);
      message.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  // Carica magazzini quando cambia il tipo di spedizione
  useEffect(() => {
    if (shipmentType) {
      loadWarehouses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipmentType]);

  const loadWarehouses = async () => {
    try {
      const warehousesRes = await unifiedShipmentsAPI.getWarehouses(shipmentType || undefined);
      setWarehouses(warehousesRes || []);
    } catch (error) {
      console.error('Error loading warehouses:', error);
    }
  };

  // Scelta esplicita del tipo spedizione (spedizioni anche multi-fornitore)
  const handleShipmentTypeChange = (type: UnifiedShipmentType) => {
    setShipmentType(type);
    form.setFieldsValue({ shipment_type: type, warehouse_id: undefined });

    // Deseleziona il fornitore principale se non compatibile col nuovo tipo
    const currentSupplierId = form.getFieldValue('supplier_id');
    const current = suppliers.find(s => s.id === currentSupplierId);
    if (current && current.origin_type?.code !== type) {
      form.setFieldsValue({ supplier_id: undefined });
      setSelectedSupplier(null);
    }
  };

  // Selezione fornitore principale (opzionale): se il tipo non è ancora scelto, lo deriva
  const handleSupplierChange = (supplierId?: number) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    setSelectedSupplier(supplier || null);

    if (supplier && !shipmentType) {
      let type: UnifiedShipmentType = 'NAZIONALE';

      // Usa origin_type.code dalla nuova struttura
      const originCode = supplier.origin_type?.code;
      switch (originCode) {
        case 'EXTRA_UE':
          type = 'EXTRA_UE';
          break;
        case 'UE':
          type = 'UE';
          break;
        case 'NAZIONALE':
        default:
          type = 'NAZIONALE';
      }

      setShipmentType(type);
      form.setFieldsValue({ shipment_type: type });

      // Reset magazzino quando cambia il tipo
      form.setFieldsValue({ warehouse_id: undefined });
    }
  };

  const getShipmentTypeIcon = (type: UnifiedShipmentType | null) => {
    switch (type) {
      case 'EXTRA_UE':
        return <GlobalOutlined style={{ color: '#1890ff' }} />;
      case 'UE':
        return <EuroCircleOutlined style={{ color: '#52c41a' }} />;
      case 'NAZIONALE':
        return <HomeOutlined style={{ color: '#fa8c16' }} />;
      default:
        return <TruckOutlined />;
    }
  };

  const getShipmentTypeColor = (type: UnifiedShipmentType | null) => {
    switch (type) {
      case 'EXTRA_UE':
        return 'blue';
      case 'UE':
        return 'green';
      case 'NAZIONALE':
        return 'orange';
      default:
        return 'default';
    }
  };

  const getShipmentTypeLabel = (type: UnifiedShipmentType | null) => {
    switch (type) {
      case 'EXTRA_UE':
        return 'Extra-UE (Container)';
      case 'UE':
        return 'Unione Europea';
      case 'NAZIONALE':
        return 'Nazionale';
      default:
        return 'Seleziona tipo spedizione';
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const data = {
        ...values,
        shipment_type: shipmentType,
        departure_date: values.departure_date?.format('YYYY-MM-DD'),
        expected_arrival_date: values.expected_arrival_date?.format('YYYY-MM-DD'),
        items: [] // Inizialmente vuoto, items aggiunti dopo
      };

      await unifiedShipmentsAPI.createUnifiedShipment(data);
      message.success('Spedizione creata con successo');
      form.resetFields();
      setShipmentType(null);
      setSelectedSupplier(null);
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error creating shipment:', error);
      message.error(error.response?.data?.detail || 'Errore durante la creazione della spedizione');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setShipmentType(null);
    setSelectedSupplier(null);
    setSelectedTransportTypeCode(null);
    onClose();
  };

  // Trova lo stato "in_preparation" di default
  const defaultStatusId = statuses.find(s => s.code === 'in_preparation')?.id || statuses[0]?.id;

  // Imposta il valore di default per status_id quando gli stati sono caricati
  useEffect(() => {
    if (statuses.length > 0 && defaultStatusId) {
      form.setFieldsValue({ status_id: defaultStatusId });
    }
  }, [statuses, defaultStatusId, form]);

  return (
    <Modal
      title={
        <span>
          <TruckOutlined style={{ marginRight: 8 }} />
          Nuova Spedizione
        </span>
      }
      open={visible}
      onOk={handleSubmit}
      onCancel={handleClose}
      width={1000}
      okText="Crea Spedizione"
      cancelText="Annulla"
      confirmLoading={submitting}
      destroyOnClose
    >
      <Spin spinning={loading}>
        {/* Tipo spedizione derivato */}
        {shipmentType && (
          <Alert
            message={
              <span>
                {getShipmentTypeIcon(shipmentType)}
                <span style={{ marginLeft: 8 }}>
                  Tipo Spedizione: <Tag color={getShipmentTypeColor(shipmentType)}>{getShipmentTypeLabel(shipmentType)}</Tag>
                </span>
              </span>
            }
            description={
              shipmentType === 'EXTRA_UE'
                ? "Spedizione da paese extra-UE. All'arrivo la merce entra con i documenti doganali: H2 (IM7) in magazzino doganale e/o H1 (IM4) in magazzino nazionalizzato (se la destinazione è nazionalizzata, solo H1 / IM4)."
                : shipmentType === 'UE'
                ? 'Spedizione da paese UE. Non richiede documenti doganali, usa magazzino nazionale.'
                : 'Spedizione nazionale. Usa magazzino nazionale.'
            }
            type={shipmentType === 'EXTRA_UE' ? 'info' : 'success'}
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Form
          form={form}
          layout="vertical"
          initialValues={{
            status_id: defaultStatusId
          }}
        >
          <Divider orientation="left">Informazioni Base</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="shipment_type"
                label="Tipo Spedizione"
                rules={[{ required: true, message: 'Seleziona il tipo spedizione' }]}
              >
                <Select placeholder="Seleziona tipo" onChange={handleShipmentTypeChange}>
                  <Option value="EXTRA_UE">Extra-UE (Container)</Option>
                  <Option value="UE">Unione Europea</Option>
                  <Option value="NAZIONALE">Nazionale</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="supplier_id"
                label="Fornitore Principale (opzionale)"
                tooltip="La spedizione può contenere ordini di più fornitori compatibili con il tipo selezionato"
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Tutti i fornitori compatibili"
                  optionFilterProp="children"
                  onChange={handleSupplierChange}
                  filterOption={(input, option) =>
                    (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {suppliers
                    .filter(supplier => !shipmentType || supplier.origin_type?.code === shipmentType)
                    .map(supplier => (
                    <Option key={supplier.id} value={supplier.id}>
                      {supplier.name}
                      {supplier.origin_type && (
                        <Tag
                          color={getShipmentTypeColor(supplier.origin_type.code as UnifiedShipmentType)}
                          style={{ marginLeft: 8 }}
                        >
                          {supplier.origin_type.description || supplier.origin_type.code}
                        </Tag>
                      )}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="shipment_code"
                label="Codice Spedizione"
                rules={[{ required: true, message: 'Inserisci codice spedizione' }]}
              >
                <Input placeholder="es. SHIP-2024-001" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="description"
                label="Descrizione"
              >
                <Input placeholder="Descrizione opzionale della spedizione" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Trasporto</Divider>

          <Row gutter={16}>
            <Col span={selectedTransportTypeCode === 'CONTAINER' ? 6 : 8}>
              <Form.Item
                name="transport_type_id"
                label="Tipo Trasporto"
                rules={[{ required: true, message: 'Seleziona tipo trasporto' }]}
              >
                <Select
                  placeholder="Seleziona tipo"
                  onChange={(value) => {
                    const transportType = transportTypes.find(t => t.id === value);
                    setSelectedTransportTypeCode(transportType?.code || null);
                    // Reset container_type_id se il tipo trasporto non è CONTAINER
                    if (transportType?.code !== 'CONTAINER') {
                      form.setFieldsValue({ container_type_id: undefined });
                    }
                  }}
                >
                  {transportTypes.map(type => (
                    <Option key={type.id} value={type.id}>
                      {type.code === 'CONTAINER' && <ContainerOutlined style={{ marginRight: 4 }} />}
                      {type.code === 'TRUCK' && <TruckOutlined style={{ marginRight: 4 }} />}
                      {type.description}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            {selectedTransportTypeCode === 'CONTAINER' && (
              <Col span={6}>
                <Form.Item
                  name="container_type_id"
                  label="Tipo Container"
                >
                  <Select placeholder="Seleziona tipo container" allowClear>
                    {containerTypes.map(ct => (
                      <Option key={ct.id} value={ct.id}>
                        {ct.description}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            )}
            <Col span={selectedTransportTypeCode === 'CONTAINER' ? 6 : 8}>
              <Form.Item
                name="transport_document_type"
                label="Tipo Documento Trasporto"
              >
                <Select placeholder="Seleziona tipo" allowClear>
                  <Option value="CMR">CMR</Option>
                  <Option value="BL">Bill of Lading (BL)</Option>
                  <Option value="AWB">Air Waybill (AWB)</Option>
                  <Option value="DDT">DDT</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={selectedTransportTypeCode === 'CONTAINER' ? 6 : 8}>
              <Form.Item
                name="transport_document_number"
                label="Numero Documento"
              >
                <Input placeholder="Numero documento trasporto" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="carrier_name"
                label="Vettore/Spedizioniere"
              >
                <Input placeholder="Nome vettore o spedizioniere" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="vehicle_plate"
                label="Targa/Numero Container"
              >
                <Input placeholder="Targa veicolo o numero container" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Date e Destinazione</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="departure_date"
                label="Data Partenza"
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="expected_arrival_date"
                label="Data Arrivo Prevista"
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="status_id"
                label="Stato"
                rules={[{ required: true, message: 'Seleziona stato' }]}
                tooltip="Lo stato iniziale è sempre 'In Preparazione'. Potrà essere modificato dopo aver aggiunto i prodotti."
              >
                <Select placeholder="Seleziona stato" disabled>
                  {statuses.map(status => (
                    <Option key={status.id} value={status.id}>
                      {status.description}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="warehouse_id"
                label="Magazzino Destinazione"
                rules={[{ required: true, message: 'Seleziona magazzino' }]}
                tooltip={shipmentType === 'EXTRA_UE'
                  ? 'Per spedizioni Extra-UE: magazzino doganale (H2/H1) o nazionalizzato (solo H1)'
                  : 'Per spedizioni UE/Nazionali seleziona un magazzino nazionale'
                }
              >
                <Select
                  placeholder={shipmentType ? "Seleziona magazzino" : "Seleziona prima un fornitore"}
                  disabled={!shipmentType}
                >
                  {warehouses.map(wh => (
                    <Option key={wh.id} value={wh.id}>
                      {wh.cod_mag && `[${wh.cod_mag}] `}{wh.description}
                      <Tag
                        color={wh.is_nationalized ? 'green' : 'blue'}
                        style={{ marginLeft: 8 }}
                      >
                        {wh.is_nationalized ? 'Nazionale' : 'Doganale'}
                      </Tag>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="importer_id"
                label="Importatore"
                tooltip="Obbligatorio per spedizioni Extra-UE"
              >
                <Select
                  placeholder="Seleziona importatore"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                >
                  {importers.map(imp => (
                    <Option key={imp.id} value={imp.id}>
                      {imp.ragione_sociale}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Note</Divider>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="notes"
                label="Note"
              >
                <TextArea rows={3} placeholder="Note aggiuntive sulla spedizione" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Spin>
    </Modal>
  );
};

export default ShipmentCreateModal;
